import { Dropdown, Table, Form, Button, Badge, Modal } from "react-bootstrap";
import { CSVLink } from "react-csv";
import DetailedIcon, {
  ExportIcon,
  FilterIcon,
  FilterTwoIcon,
  BusinessBillIcon,
  PrintIcon,
  SummaryChartIconUp,
  SummaryChartIconDown,
  SummaryCloseIcon,
  FilterCollapseIcon,
  ExchangeFunds,
  ExcelIcon,
  PDFIcon,
  DeleteIcon,
  EditIcon,
  RecieptIcon,
  DropdownCloseIcon,
  DropdownEyeIcon,
} from "../Icons";
import "./../../assets/scss/reports/cashbook.scss";
import { useEffect, useRef, useState, useMemo } from "react";
import CachedIcon from "mdi-react/CachedIcon";
import Select from "react-select";
import useDebounce, {
  useIsAdmin,
  useLocalStorage,
  useQueryParams,
  useReconcilation,
  useScrollTop,
} from "../../utils/hooks";
import { services } from "../../config";
import { reportActions } from "../../utils/reactQueryActions";
import { useMutation, useQuery } from "react-query";
import currency from "currency.js";
import { format, parse } from "date-fns";
import ReactPaginate from "react-paginate";
import {
  convertMomentDate,
  customerFullName,
  defaultSelectValue,
  formatDate,
  paginationOptions,
  reactSelectTheme,
} from "../../utils/helpers";
import RsDateRangePicker from "../utils/RsDateRangePicker";
import queryString from "query-string";
import { useAuth } from "../../hooks/useAuth";
import { isEmpty, uniqBy } from "lodash";
import NoTableItem from "../utils/NoTableItem";
import { useDownloadExcel } from "../../hooks/useDownloadExcel";
import ModalLoader from "../utils/ModalLoader";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import { toast } from "react-toastify";
import ConfirmDialog from "../ConfirmDialogue";
import ReconcilationCheckbox from "../utils/ReconcilationCheckbox";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik, FieldArray, FormikProvider } from "formik";
import CreatableSelect from "react-select/creatable";
import JobItemsModal from "./JobItems";
import * as XLSX from "xlsx";
import Datetime from "react-datetime";
import { Popover } from "react-tiny-popover";
import DatePickerCustomInput from "../utils/DatePickerCustomInput";
import EyeOutlineIcon from "mdi-react/EyeOutlineIcon";

function Attachments({ files = [], setFiles, milestone }) {
  const { backendUrl } = useAuth();
  return (
    <div>
      <div>
        {files?.map((el, index) => (
          <div key={index} className="d-flex align-items-center">
            {el?.fileId ? (
              <a
                href={`${backendUrl}/api/job/view-certificate/${
                  el.systemFileName
                }`}
                target="_blank"
                rel="noreferrer"
                className="text-primary"
              >
                {el?.name}
              </a>
            ) : (
              <span>{el?.name}</span>
            )}

            <Button
              variant=""
              size="sm"
              onClick={e => setFiles(files.filter((el, i) => i !== index))}
            >
              <DropdownCloseIcon width={16} />
            </Button>
            <hr className="m-0" />
          </div>
        ))}
      </div>

      <Form.Label className="btn btn-light-blue">
        <span>Add Files </span>
        <Form.Control
          type="file"
          className="d-none"
          multiple
          onChange={e => setFiles([...files, ...e.target.files])}
        />
      </Form.Label>
    </div>
  );
}

function NewMileStoneRow({
  selectedJob,
  systemMilestones = [],
  refetch,
  onCancel,
  refetchSelectedJob,
}) {
  const { backendUrl } = useAuth();
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showDueDatePicker, setShowDueDatePicker] = useState(false);

  const normalizeDateValue = value => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (value?._isAMomentObject && value?.format) {
      return value.format("YYYY-MM-DD");
    }
    if (value instanceof Date) {
      return format(value, "yyyy-MM-dd");
    }
    return `${value}`;
  };

  const formik = useFormik({
    initialValues: {
      milestone: "",
      files: [],
      jobId: selectedJob.id,
      milestoneNumber: String(Date.now()),
      startDate: new Date(),
      dueDate: new Date(),
    },
    validationSchema: yup.object().shape({
      milestone: yup.string().required("Milestone is required"),
    }),
    onSubmit: values => {
      uploadCertificateMutation.mutate({
        ...values,
        startDate: normalizeDateValue(values.startDate),
        dueDate: normalizeDateValue(values.dueDate),
      });
    },
  });

  const uploadCertificate = async payload => {
    const formData = new FormData();

    for (const [key, value] of Object.entries(payload)) {
      if (key === "files") {
        for (const file of value) {
          if (!file?.fileId) {
            formData.append("files", file);
          }
        }
        continue;
      }
      formData.append(key, value);
    }

    let response = await fetch(`${backendUrl}/api/job/upload-certificate`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    return data;
  };

  const uploadCertificateMutation = useMutation(
    payload => uploadCertificate(payload),
    {
      onSuccess: () => {
        toast.success("Milestone added successfully");
        onCancel();
        refetch();
        if (refetchSelectedJob) {
          refetchSelectedJob();
        }
      },
      onError: err => {
        toast.error(err?.message || "Unable to create milestone");
      },
    }
  );

  return (
    <tr className="bg-light-blue">
      <td>{""}</td>
      <td>{formatDate(new Date())}</td>
      <td>
        <CreatableSelect
          styles={{
            container: provided => ({
              ...provided,
              minWidth: "280px",
            }),
          }}
          classNamePrefix="form-select"
          options={systemMilestones}
          value={systemMilestones.find(
            el => el.value === formik.values.milestone
          )}
          onChange={option =>
            formik.setFieldValue("milestone", option?.value || "")
          }
          isSearchable
          menuPosition="fixed"
        />
        {formik.touched.milestone && formik.errors.milestone && (
          <small className="text-danger">{formik.errors.milestone}</small>
        )}
      </td>
      <td>
        <Popover
          reposition={false}
          isOpen={showStartDatePicker}
          onClickOutside={() => setShowStartDatePicker(false)}
          align="end"
          padding={10}
          content={() => (
            <Datetime
              dateFormat="MM DD, YYYY"
              className="date-picker-2"
              timeFormat={false}
              closeOnSelect={true}
              closeOnClickOutside={true}
              name="startDate"
              inputProps={{
                className: "date-input form-control",
                placeholder: "Select date",
                readOnly: true,
              }}
              input={false}
              value={formik.values.startDate}
              onChange={date => {
                formik.setFieldValue("startDate", date, true);
                setShowStartDatePicker(false);
              }}
            />
          )}
        >
          <DatePickerCustomInput
            onClick={() => setShowStartDatePicker(!showStartDatePicker)}
            value={convertMomentDate(formik.values.startDate)}
          />
        </Popover>
      </td>
      <td>
        <Popover
          reposition={false}
          isOpen={showDueDatePicker}
          onClickOutside={() => setShowDueDatePicker(false)}
          align="end"
          padding={10}
          content={() => (
            <Datetime
              dateFormat="MM DD, YYYY"
              className="date-picker-2"
              timeFormat={false}
              closeOnSelect={true}
              closeOnClickOutside={true}
              name="dueDate"
              inputProps={{
                className: "date-input form-control",
                placeholder: "Select date",
                readOnly: true,
              }}
              input={false}
              value={formik.values.dueDate}
              onChange={date => {
                formik.setFieldValue("dueDate", date, true);
                setShowDueDatePicker(false);
              }}
            />
          )}
        >
          <DatePickerCustomInput
            onClick={() => setShowDueDatePicker(!showDueDatePicker)}
            value={convertMomentDate(formik.values.dueDate)}
          />
        </Popover>
      </td>
      <td>
        <Attachments
          files={formik.values.files}
          setFiles={files => formik.setFieldValue("files", files)}
        />
      </td>
      <td>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            type="button"
            onClick={() => formik.handleSubmit()}
            disabled={uploadCertificateMutation.isLoading}
          >
            Add
          </Button>
          <Button variant="light" size="sm" type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </td>
    </tr>
  );
}

export function MileStoneDetails({
  milestone,
  systemMilestones = [],
  selectedJob,
  refetch,
  refetchSelectedJob,
}) {
  const { backendUrl } = useAuth();
  const autoSaveTimeoutRef = useRef();
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showDueDatePicker, setShowDueDatePicker] = useState(false);

  const normalizeDateValue = value => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (value?._isAMomentObject && value?.format) {
      return value.format("YYYY-MM-DD");
    }
    if (value instanceof Date) {
      return format(value, "yyyy-MM-dd");
    }
    return `${value}`;
  };

  const formik = useFormik({
    initialValues: {
      milestone: milestone.milestone,
      files: milestone.files,
      jobId: selectedJob.id,
      milestoneNumber: milestone.milestoneNumber,
      startDate: milestone.startDate || new Date(),
      dueDate: milestone.dueDate || milestone.endDate || new Date(),
    },
    validationSchema: yup.object().shape({}),
    onSubmit: values => {
      uploadCertificateMutation.mutate({
        ...values,
        startDate: normalizeDateValue(values.startDate),
        dueDate: normalizeDateValue(values.dueDate),
        endDate: normalizeDateValue(values.dueDate),
      });
    },
  });

  const uploadCertificate = async payload => {
    const formData = new FormData();
    for (const [key, value] of Object.entries(payload)) {
      if (key === "files") {
        for (const file of value) {
          if (!file?.fileId) {
            formData.append("files", file);
          }
        }
        continue;
      }
      formData.append(key, value);
    }
    formData.append(
      "alreadyUploadedFiles",
      JSON.stringify(payload.files.filter(el => el.fileId))
    );

    let response = await fetch(`${backendUrl}/api/job/update-milestone`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    return data;
  };

  const uploadCertificateMutation = useMutation(
    payload => uploadCertificate(payload),
    {
      onSuccess: data => {
        formik.resetForm({ values: formik.values });
        toast.success("Saved");
        refetch();
        if (refetchSelectedJob) {
          refetchSelectedJob();
        }
      },
      onError: err => {
        toast.error(err?.message || "Unable to Upload");
      },
    }
  );

  useEffect(() => {
    if (!formik.dirty || uploadCertificateMutation.isLoading) {
      return;
    }

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      formik.submitForm();
    }, 1200);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [formik.values, formik.dirty, uploadCertificateMutation.isLoading]);

  const deleteMileStone = async payload => {
    let response = await fetch(`${backendUrl}/api/job/delete-milestone`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(payload),
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };

  const deleteMutation = useMutation(payload => deleteMileStone(payload), {
    onSuccess: ({ message }) => {
      toast.success(message);
    },
    onError: ({ message = "" }) => {
      toast.error(`Unable to perform action: ${message}`);
    },
  });

  const handleDeleteMilestone = async milestone => {
    if (
      await ConfirmDialog({
        title: "Delete",
        description: "Are you sure you want to delete",
      })
    ) {
      deleteMutation.mutate({
        jobId: selectedJob.id,
        milestoneNumber: milestone.milestoneNumber,
      });
    }
  };

  return (
    <tr>
      <td>
        <Dropdown>
          <Dropdown.Toggle
            variant=""
            className="bg-white border-0"
            bsPrefix="print more"
          >
            <DotsVerticalIcon />
          </Dropdown.Toggle>
          <Dropdown.Menu
            popperConfig={{
              strategy: "fixed",
            }}
            renderOnMount
            className="dropdown-with-icons"
          >
            <Dropdown.Item
              as={Link}
              target="_blank"
              to={`/reports/job-income-statement?page=1&limit=100&Description=${encodeURIComponent(
                selectedJob.title
              )}&AccountID=${encodeURIComponent(
                selectedJob.jobNumber
              )}&milestoneNumber=${encodeURIComponent(
                milestone.milestoneNumber
              )}`}
            >
              <EyeOutlineIcon /> View Report
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </td>

      <td>{formatDate(milestone.date)}</td>

      <td>
        {selectedJob.hasMilestone && (
          <CreatableSelect
            styles={{
              container: provided => ({
                ...provided,
                minWidth: "280px",
              }),
            }}
            classNamePrefix="form-select"
            options={systemMilestones}
            value={systemMilestones.find(
              el => el.value === formik.values.milestone
            )}
            onChange={({ value }) => formik.setFieldValue(`milestone`, value)}
            isSearchable
            menuPosition="fixed"
          />
        )}
      </td>

      <td>
        <Popover
          reposition={false}
          isOpen={showStartDatePicker}
          onClickOutside={() => setShowStartDatePicker(false)}
          align="end"
          padding={10}
          content={() => (
            <Datetime
              dateFormat="MM DD, YYYY"
              className="date-picker-2"
              timeFormat={false}
              closeOnSelect={true}
              closeOnClickOutside={true}
              name="startDate"
              inputProps={{
                className: `date-input form-control ${
                  formik.touched.startDate && !!formik.errors.startDate
                    ? "is-invalid"
                    : ""
                }`,
                placeholder: "Select date",
                readOnly: true,
              }}
              input={false}
              value={formik.values.startDate}
              onChange={date => {
                formik.setFieldValue("startDate", date, true);
                setShowStartDatePicker(false);
              }}
            />
          )}
        >
          <DatePickerCustomInput
            onClick={() => setShowStartDatePicker(!showStartDatePicker)}
            value={convertMomentDate(formik.values.startDate)}
          />
        </Popover>
      </td>
      <td>
        <Popover
          reposition={false}
          isOpen={showDueDatePicker}
          onClickOutside={() => setShowDueDatePicker(false)}
          align="end"
          padding={10}
          content={() => (
            <Datetime
              dateFormat="MM DD, YYYY"
              className="date-picker-2"
              timeFormat={false}
              closeOnSelect={true}
              closeOnClickOutside={true}
              name="dueDate"
              inputProps={{
                className: `date-input form-control ${
                  formik.touched.dueDate && !!formik.errors.dueDate
                    ? "is-invalid"
                    : ""
                }`,
                placeholder: "Select date",
                readOnly: true,
              }}
              input={false}
              value={formik.values.dueDate}
              onChange={date => {
                formik.setFieldValue("dueDate", date, true);
                setShowDueDatePicker(false);
              }}
              onBlur={() => formik.setFieldTouched("dueDate", true)}
            />
          )}
        >
          <DatePickerCustomInput
            onClick={() => setShowDueDatePicker(!showDueDatePicker)}
            value={convertMomentDate(formik.values.dueDate)}
          />
        </Popover>
      </td>
      <td>
        <Attachments
          files={formik.values.files}
          setFiles={files => formik.setFieldValue("files", files)}
          milestone={milestone}
        />
      </td>
      <td>
        <div className="d-flex flex-column gap-1 align-items-start">
          <div className="d-flex gap-3">
            <Button
              variant="outline-primary"
              size="sm"
              type="button"
              onClick={() => formik.handleSubmit()}
              disabled={uploadCertificateMutation.isLoading}
            >
              Save
            </Button>

            <Button
              variant=""
              size="sm"
              type="button"
              onClick={() => handleDeleteMilestone(milestone)}
              className="me-2"
            >
              <DeleteIcon />
            </Button>
          </div>

          <div className="position-relative">
            <div className="position-absolute p-0 text-nowrap">
              {formik.dirty && !uploadCertificateMutation.isLoading && (
                <small className="text-muted">
                  Changes detected. Auto-saving...
                </small>
              )}
              {uploadCertificateMutation.isLoading && (
                <small className="text-muted">Saving...</small>
              )}
              {uploadCertificateMutation.isSuccess && !formik.dirty && (
                <small className="text-success">Saved</small>
              )}
              {uploadCertificateMutation.isError && (
                <small className="text-danger">
                  {uploadCertificateMutation.error?.message ||
                    "Unable to autosave changes"}
                </small>
              )}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function MileStoneModal({
  selectedJob,
  setSelectedJob,
  systemMilestones = [],
  refetch: refetchSelectedJob,
}) {
  const { backendUrl } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);

  const fetchJobDetails = async () => {
    let response = await fetch(
      `${backendUrl}/api/job/job-details/${selectedJob.jobNumber}?jobNumber=${
        selectedJob.jobNumber
      }`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    data.milestones = data?.milestones.map(el => ({
      ...el,
      label: el.milestone,
      value: el.milestone,
    }));
    return data;
  };

  const {
    data = { job: {}, milestones: [] },
    refetch,
    isFetching,
    isSuccess,
    isFetched,
  } = useQuery(["_JOB_DETAILS"], () => fetchJobDetails(), {
    keepPreviousData: false,
  });

  const filesByMileStone = useMemo(() => {
    if (!data.job) return [];
    const files = data.job?.files ? JSON.parse(data.job?.files) : [];
    const milestones = data.job?.milestones
      ? JSON.parse(data.job?.milestones)
      : [];

    return milestones.map(el => ({
      milestone: el.milestone,
      milestoneNumber: el.milestoneNumber,
      date: el.date,
      startDate: el.startDate || "",
      dueDate: el.dueDate || el.endDate || "",
      files: files.filter(file => file.milestoneNumber === el.milestoneNumber),
    }));
  }, [data.job]);

  const normalizedMilestones = useMemo(
    () => uniqBy(systemMilestones, "milestone"),
    [systemMilestones]
  );

  const handleExportExcel = () => {
    const exportData = filesByMileStone.map(el => ({
      Date: formatDate(el.date),
      "Start Date": formatDate(el.startDate),
      "Due Date": formatDate(el.dueDate),
      Milestone: el.milestone,
      "File Count": el.files.length,
      Files: el.files.map(f => f.name).join(", "),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Milestones");
    XLSX.writeFile(workbook, `${selectedJob.title}-Milestones.xlsx`);
  };

  return (
    <>
      <Modal
        show={true}
        animation={false}
        centered
        enforceFocus={false}
        onHide={() => setSelectedJob()}
        fullscreen
      >
        <Modal.Header closeButton>
          <div className="d-flex justify-content-between align-items-center w-100">
            <h2 className="h5">Milestones</h2>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-4">
            <div className="d-flex gap-3 w-100 justify-content-between align-items-top">
              <h3 className="h5">{selectedJob?.title}</h3>{" "}
              <div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleExportExcel}
                  disabled={isEmpty(filesByMileStone)}
                >
                  <ExcelIcon className="me-2" /> Export to Excel
                </Button>
              </div>
            </div>
            <p>{selectedJob?.description}</p>
          </div>

          {!isEmpty(filesByMileStone) ? (
            <Table hover striped responsive className="product-table">
              <thead>
                <tr>
                  <th>&nbsp;</th>
                  <th>Post Date</th>
                  <th style={{ minWidth: "280px" }}>Milestone</th>
                  <th>Start Date</th>
                  <th>Due Date</th>
                  <th>Files</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filesByMileStone.map((el, index) => (
                  <MileStoneDetails
                    key={index}
                    milestone={el}
                    systemMilestones={normalizedMilestones}
                    selectedJob={selectedJob}
                    refetch={() => refetch()}
                    refetchSelectedJob={() => refetchSelectedJob()}
                  />
                ))}

                {showCreateForm && (
                  <NewMileStoneRow
                    selectedJob={selectedJob}
                    systemMilestones={normalizedMilestones}
                    refetch={() => refetch()}
                    onCancel={() => setShowCreateForm(false)}
                    refetchSelectedJob={() => refetchSelectedJob()}
                  />
                )}
              </tbody>
            </Table>
          ) : (
            <>
              {showCreateForm ? (
                <Table hover striped responsive className="product-table">
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <th>Post Date</th>
                      <th style={{ minWidth: "280px" }}>Milestone</th>
                      <th>Start Date</th>
                      <th>Due Date</th>
                      <th>Files</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <NewMileStoneRow
                      selectedJob={selectedJob}
                      systemMilestones={normalizedMilestones}
                      refetch={() => refetch()}
                      onCancel={() => setShowCreateForm(false)}
                    />
                  </tbody>
                </Table>
              ) : (
                <NoTableItem />
              )}
            </>
          )}

          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowCreateForm(true)}
            disabled={showCreateForm}
          >
            + Add Milestone
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
}
